import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { CategorySelect } from '../components/CategorySelect'
import { LabelChip, type LabelLike } from '../components/LabelChip'
import { LabelPicker } from '../components/LabelPicker'
import type { CrmCategoryRef } from '../types'
import { createOrganisation, matchOrganisations } from './api'
import { MATCH_REASON_LABELS, type CrmOrganisation, type CrmOrganisationMatch } from './types'

const DRAFT_KEY = 'crm:nieuwe-organisatie'

interface Draft {
  name: string
  website: string
  city: string
}

function readDraft(): Draft {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY)
    if (raw) return { name: '', website: '', city: '', ...(JSON.parse(raw) as Partial<Draft>) }
  } catch {
    // An unreadable draft is no reason to block the form.
  }
  return { name: '', website: '', city: '' }
}

function writeDraft(draft: Draft | null) {
  try {
    if (draft) sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
    else sessionStorage.removeItem(DRAFT_KEY)
  } catch {
    // Private mode or a full storage: the form still works, only without a draft.
  }
}

const fieldClass = 'flex flex-col gap-1.5 text-sm font-medium'

/**
 * "Nieuwe organisatie" (design §4.3): name, website, city, category, owner and
 * labels, with a "Lijkt op …" warning from GET /crm/organisations/match while you
 * type. The draft survives closing the sheet (sessionStorage, IA-15).
 */
export function NewOrganisationSheet({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (organisation: CrmOrganisation) => void
}) {
  const [draft, setDraft] = useState<Draft>(readDraft)
  const [category, setCategory] = useState<CrmCategoryRef | null>(null)
  const [ownerId, setOwnerId] = useState('')
  const [labels, setLabels] = useState<LabelLike[]>([])
  const [matches, setMatches] = useState<CrmOrganisationMatch[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const timer = useRef<number | undefined>(undefined)
  /** Numbers the look-alike lookups: an answer that is not for the newest one is dropped. */
  const lookup = useRef(0)
  const submitting = useRef(false)
  const { membersById } = useTeamMembers()
  const team = membersById
    ? [...membersById.values()].sort((a, b) => a.display_name.localeCompare(b.display_name, 'nl'))
    : []

  useEffect(() => () => window.clearTimeout(timer.current), [])

  function update(next: Draft) {
    setDraft(next)
    writeDraft(next)
    // An earlier "Naam is verplicht." or failed save is about what was there before.
    setError(null)
    window.clearTimeout(timer.current)
    const id = ++lookup.current
    if (next.name.trim().length < 2 && !next.website.trim()) {
      setMatches([])
      return
    }
    timer.current = window.setTimeout(() => {
      matchOrganisations({
        name: next.name.trim() || undefined,
        website: next.website.trim() || undefined,
      })
        .then((found) => {
          if (id === lookup.current) setMatches(found)
        })
        .catch(() => {
          if (id === lookup.current) setMatches([])
        })
    }, 300)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (submitting.current) return
    if (!draft.name.trim()) {
      setError('Naam is verplicht.')
      return
    }
    submitting.current = true
    setSaving(true)
    setError(null)
    try {
      const created = await createOrganisation({
        name: draft.name.trim(),
        website: draft.website.trim() || null,
        city: draft.city.trim() || null,
        category_id: category?.id ?? null,
        owner_user_id: ownerId || null,
        label_ids: labels.map((label) => label.id),
      })
      writeDraft(null)
      // A lookup that is still on its way is about the form as it was.
      window.clearTimeout(timer.current)
      lookup.current += 1
      setDraft({ name: '', website: '', city: '' })
      setCategory(null)
      setOwnerId('')
      setLabels([])
      setMatches([])
      onCreated(created)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Opslaan mislukt.')
    } finally {
      submitting.current = false
      setSaving(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="md" aria-describedby={undefined}>
        <SheetTitle className="text-lg">Nieuwe organisatie</SheetTitle>
        <form className="mt-2 flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
          <label className={fieldClass}>
            Naam *
            <Input
              value={draft.name}
              onChange={(event) => update({ ...draft, name: event.target.value })}
              aria-label="Naam"
              maxLength={200}
              autoFocus
            />
          </label>
          {matches.length > 0 ? (
            <div role="status" className="rounded-lg border border-warning-border bg-warning-subtle p-3 text-sm">
              <p className="font-medium text-warning">Lijkt op een bestaande organisatie:</p>
              <ul className="mt-1 flex flex-col gap-1">
                {matches.map((match) => (
                  <li key={match.id}>
                    <Link
                      to={`/relaties/organisaties/${match.id}`}
                      className="font-medium underline-offset-2 hover:underline"
                    >
                      {match.name}
                    </Link>
                    <span className="text-warning"> · {MATCH_REASON_LABELS[match.reason]}</span>
                    {match.city ? <span className="text-muted-foreground"> · {match.city}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <label className={fieldClass}>
            Website
            <Input
              value={draft.website}
              onChange={(event) => update({ ...draft, website: event.target.value })}
              aria-label="Website"
              placeholder="voorbeeld.nl"
            />
          </label>
          <label className={fieldClass}>
            Plaats
            <Input
              value={draft.city}
              onChange={(event) => update({ ...draft, city: event.target.value })}
              aria-label="Plaats"
            />
          </label>
          <div className={fieldClass}>
            <span>Categorie</span>
            <CategorySelect value={category} onChange={setCategory} />
          </div>
          <label className={fieldClass}>
            Eigenaar
            <select
              value={ownerId}
              onChange={(event) => setOwnerId(event.target.value)}
              aria-label="Eigenaar"
              className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
            >
              <option value="">Geen eigenaar</option>
              {team.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.display_name}
                </option>
              ))}
            </select>
          </label>
          <div className={fieldClass}>
            <span>Labels</span>
            <div className="flex flex-wrap items-center gap-1">
              {labels.map((label) => (
                <LabelChip
                  key={label.id}
                  label={label}
                  onRemove={() => setLabels(labels.filter((item) => item.id !== label.id))}
                />
              ))}
              <LabelPicker selected={labels} onChange={setLabels} />
            </div>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuleren
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Opslaan…' : 'Organisatie aanmaken'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
